import { Suspense } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Add, Delete, Download, Pencil } from '@factorialco/factorial-one-react/icons/app'
import {
  ProjectForSelectFragment,
  SubprojectDataCollectionItemFragment,
  SubprojectsDataCollectionDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

interface SubprojectsDataCollectionProps {
  projectId: number
  canExportData: boolean
  canManageSubprojects: boolean
  canSeeCosts: boolean
  projectCurrency: string
  onClick: (newData: ISubprojectsPanelData) => void
}

const SubprojectsDataCollection = ({
  projectId,
  canExportData,
  canManageSubprojects,
  canSeeCosts,
  projectCurrency,
  onClick,
}: SubprojectsDataCollectionProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { hasExpenses, hasFlexibleTracking, project } = useOutletContext<{
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    project: ProjectForSelectFragment
  }>()

  const itemOnClick = (item: SubprojectDataCollectionItemFragment) => () => {
    onClick({
      inputedMinutes: item.inputedMinutes ?? 0,
      laborCostCents: item.laborCostCents ?? 0,
      subprojectId: item.id ?? 0,
      subprojectName: item.name ?? '',
    })
  }

  const source = useGraphqlDataSource({
    document: SubprojectsDataCollectionDocument,
    mapData: (data) => data.projectManagement.subprojectsConnection.nodes,
    mapVariables: ({ search, sortings }) => ({
      projectIds: [projectId],
      includeCost: canSeeCosts,
      name: search,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.subprojectsConnection.totalCount ?? 0,
    }),
    itemOnClick: itemOnClick,
    itemActions: (item) => {
      if (item.id !== null) {
        return [
          {
            label: i18n.t('project_management.table.subprojects_list.menu_links.edit_name'),
            icon: Pencil,
            onClick: () => {
              track(trackingEvents.projectManagement.subprojects.editSubprojectStart, {
                project_id: projectId,
              })

              navigate(
                navigation.projects.id.details.subprojects.subprojectId.edit.fullPath({
                  id: projectId,
                  subprojectId: Number(item.id),
                }),
                { state: { subprojectName: item.name } }
              )
            },
          },
          {
            label: i18n.t('project_management.table.subprojects_list.menu_links.delete_subproject'),
            icon: Delete,
            critical: true,
            onClick: () => {
              track(trackingEvents.projectManagement.subprojects.deleteSubprojectStart, {
                project_id: projectId,
              })

              navigate(
                navigation.projects.id.details.subprojects.subprojectId.delete.fullPath({
                  id: projectId,
                  subprojectId: Number(item.id),
                }),
                { state: { subprojectName: item.name } }
              )
            },
          },
        ]
      }

      return undefined
    },
    primaryActions: () =>
      canManageSubprojects
        ? {
            label: i18n.t('project_management.subprojects.add_button'),
            icon: Add,
            onClick: () => {
              track(trackingEvents.projectManagement.subprojects.addSubprojectStart, {
                project_id: Number(projectId),
              })

              navigate(
                navigation.projects.id.details.subprojects.create.fullPath({
                  id: Number(projectId),
                })
              )
            },
          }
        : undefined,
    secondaryActions: () =>
      canExportData
        ? [
            {
              label: i18n.t('project_management.table.projects_list.table_options.export.title'),
              description: i18n.t(
                'project_management.table.projects_list.table_options.export.description'
              ),
              icon: Download,
              onClick: () => {
                navigate(
                  navigation.projects.id.details.subprojects.export.fullPath({ id: projectId }),
                  { state: { projectForSelect: project } }
                )
              },
            },
          ]
        : undefined,
    search: {
      enabled: true,
    },
    sortings: {
      name: {
        label: i18n.t('project_management.table.subprojects_list.headers.subproject'),
      },
    },
    defaultSorting: {
      field: 'name',
      order: 'asc',
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.table.subprojects_list.headers.subproject'),
                  render: (item: SubprojectDataCollectionItemFragment) => ({
                    type: 'text',
                    value: item.name,
                  }),
                  sorting: 'name',
                },
                {
                  label: i18n.t('project_management.table.subprojects_list.headers.hours'),
                  render: (item: SubprojectDataCollectionItemFragment) => ({
                    type: 'text',
                    value: formatProjectHours(item.inputedMinutes),
                  }),
                },
                canSeeCosts
                  ? {
                      label: i18n.t('project_management.table.subprojects_list.headers.labor_cost'),
                      render: (item: SubprojectDataCollectionItemFragment) => ({
                        type: 'text',
                        value:
                          item.laborCostCents && isCurrency(projectCurrency)
                            ? MoneyWithCurrency({
                                optionalDecimals: true,
                                amount: item.laborCostCents ?? 0,
                                currency: projectCurrency,
                                locale,
                              })
                            : '-',
                      }),
                    }
                  : null,
              ]),
            },
          },
        ]}
      />
      <Suspense>
        <Outlet context={{ hasExpenses, hasFlexibleTracking }} />
      </Suspense>
    </>
  )
}

export default SubprojectsDataCollection
